/**
 * Stack → Deployment Target mapping (multi-layer).
 * Single source of truth — component reads from this map, no stack logic inline.
 *
 * Each project has three deployment layers:
 *   - frontend: Where the web UI is hosted (Netlify, Vercel, etc.)
 *   - api: Where the backend/API runs (Railway, Vercel, Render, etc.)
 *   - database: Where the database lives (Supabase is the default)
 *
 * Add new stacks here as they are introduced.
 */

// ─── Layer Option Types ─────────────────────────────────────────

export interface LayerOption {
  label: string;
  value: string;
}

export interface DeploymentTargets {
  frontend: string;
  api: string;
  database: string;
}

// ─── Per-Layer Options ──────────────────────────────────────────

export const FRONTEND_OPTIONS: LayerOption[] = [
  { label: 'Netlify',         value: 'netlify' },
  { label: 'Vercel',          value: 'vercel' },
  { label: 'Cloudflare Pages', value: 'cloudflare-pages' },
  { label: 'Railway (static)', value: 'railway' },
  { label: 'Custom',          value: 'custom' },
];

export const API_OPTIONS: LayerOption[] = [
  { label: 'Railway',     value: 'railway' },
  { label: 'Vercel',      value: 'vercel' },
  { label: 'Render',      value: 'render' },
  { label: 'Fly.io',      value: 'fly' },
  { label: 'Custom',      value: 'custom' },
];

export const DATABASE_OPTIONS: LayerOption[] = [
  { label: 'Supabase',     value: 'supabase' },
  { label: 'PlanetScale',  value: 'planetscale' },
  { label: 'Neon',         value: 'neon' },
  { label: 'Railway Postgres', value: 'railway-pg' },
  { label: 'Custom',       value: 'custom' },
];

// ─── Stack → Default Targets ────────────────────────────────────

export const STACK_DEPLOY_MAP: Record<string, DeploymentTargets> = {
  'lumeniq-default': { frontend: 'netlify',  api: 'railway', database: 'supabase' },
  'node-typescript':  { frontend: 'netlify',  api: 'railway', database: 'supabase' },
  'nextjs':           { frontend: 'vercel',   api: 'railway', database: 'supabase' },
  'python-only':      { frontend: 'netlify',  api: 'railway', database: 'supabase' },
};

/** Fallback defaults when no stack is selected or stack is unknown */
export const DEFAULT_TARGETS: DeploymentTargets = {
  frontend: 'netlify',
  api: 'railway',
  database: 'supabase',
};

/** Get the default deployment targets for a given tech stack */
export function getDefaultTargets(stack: string): DeploymentTargets | null {
  return STACK_DEPLOY_MAP[stack] ?? null;
}

// ─── Display Helpers ────────────────────────────────────────────

/** Format a DeploymentTargets object as a human-readable summary */
export function formatTargetsSummary(targets: DeploymentTargets): string {
  const parts: string[] = [];
  const frontendLabel = FRONTEND_OPTIONS.find(o => o.value === targets.frontend)?.label ?? targets.frontend;
  const apiLabel = API_OPTIONS.find(o => o.value === targets.api)?.label ?? targets.api;
  const dbLabel = DATABASE_OPTIONS.find(o => o.value === targets.database)?.label ?? targets.database;

  if (targets.frontend === targets.api) {
    parts.push(`${frontendLabel} (full-stack)`);
  } else {
    parts.push(`${frontendLabel} + ${apiLabel}`);
  }
  parts.push(dbLabel);
  return parts.join(' / ');
}

// ─── Legacy Compat ──────────────────────────────────────────────
// Keep the old single-value interface for backward compatibility
// until all consumers are migrated.

export interface DeploymentTargetOption {
  label: string;
  value: string;
  api: string | null;
  web: string | null;
}

export const DEPLOY_TARGET_OPTIONS: DeploymentTargetOption[] = [
  { label: 'Railway + Netlify',       value: 'railway+netlify', api: 'railway',  web: 'netlify' },
  { label: 'Railway (full-stack)',     value: 'railway',         api: 'railway',  web: 'railway' },
  { label: 'Vercel + Railway',        value: 'vercel+railway',  api: 'railway',  web: 'vercel' },
  { label: 'Vercel (full-stack)',      value: 'vercel',          api: 'vercel',   web: 'vercel' },
  { label: 'Render + Netlify',        value: 'render+netlify',  api: 'render',   web: 'netlify' },
  { label: 'Custom',                  value: 'custom',          api: null,        web: null },
];

/** @deprecated Use getDefaultTargets instead */
export function getDefaultTarget(stack: string): DeploymentTargetOption | null {
  const targets = STACK_DEPLOY_MAP[stack];
  if (!targets) return null;
  return DEPLOY_TARGET_OPTIONS.find(o => o.api === targets.api && o.web === targets.frontend) ?? null;
}
